import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import { RadioItemYesNo } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useBrands } from '@/models/brand';
import { useCreateDraftRequestArticle } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { BrandInputs } from './BrandInputs';
import { PleaseSelectAnOptionError } from './PleaseSelectAnOptionError';

export const ServiceEligibilityModal = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();

  const { request } = useRequestContext();

  const { data: { brands } = { brands: [] } } = useBrands({
    organizationId: request.organizationId,
    limit: 500,
    offset: 0,
  });

  const {
    mutateAsync: createArticle,
    isPending: isPendingCreateArticle,
    isSuccess: isSuccessCreateArticle,
    reset: resetCreateArticle,
  } = useCreateDraftRequestArticle(request.id);

  const [wasPurchasedWithinNetwork, setWasPurchasedWithinNetwork] = useState<
    'yes' | 'no' | undefined
  >(undefined);

  const [brandId, setBrandId] = useState<string | undefined>(undefined);
  const [otherBrandName, setOtherBrandName] = useState<string | undefined>(undefined);

  const [itemUnderWarranty, setItemUnderWarranty] = useState<'yes' | 'no' | undefined>(undefined);

  const [showIneligibilityAlert, setShowIneligibilityAlert] = useState<boolean>(false);

  const [acceptedAsException, setAcceptedAsException] = useState<boolean>(false);

  const [exceptionReason, setExceptionReason] = useState<string | undefined>(undefined);

  const [showErrors, setShowErrors] = useState<boolean>(false);

  const purchasedWithinNetworkLabel = t({
    id: 'service-eligibiliy-modal.purchased-within-network.label',
    message: 'Was the item purchased within the network?',
  });

  const itemUnderWarrantyLabel = t({
    id: 'service-eligibiliy-modal.item-under-warranty.label',
    message: 'Is the item still under warranty?',
  });

  const acceptAsExceptionLabel = t({
    id: 'service-eligibiliy-modal.accept-as-exception.label',
    message: 'Accept this item as an exception',
  });

  const addExceptionReasonLabel = t({
    id: 'service-eligibiliy-modal.accept-as-exception.add-reason.label',
    message: 'Add an exception reason',
  });

  const handleCreateArticle = async () => {
    const brand = brands.find((brand) => brand.id === brandId);

    const shouldShowErrors =
      wasPurchasedWithinNetwork === undefined ||
      !brandId ||
      (brand?.isOtherBrand && !otherBrandName) ||
      (wasPurchasedWithinNetwork === 'no' &&
        brand?.contractualObligationToHandle &&
        itemUnderWarranty === undefined) ||
      (acceptedAsException && !exceptionReason);

    if (shouldShowErrors) {
      setShowErrors(true);

      return;
    }

    if (brandId) {
      try {
        const { id } = await createArticle({
          brandId,
          otherBrandName,
          soldThroughNetwork: wasPurchasedWithinNetwork === 'yes',
          warranty:
            itemUnderWarranty === 'yes' ? true : itemUnderWarranty === 'no' ? false : undefined,
          acceptedAsException,
          exceptionReason,
        });

        handleCloseModal();

        navigate(`/requests/new/${request.id}/articles/${id}`, {
          state: { from: 'request', isNew: true, skipRouterBlocker: true },
          replace: true,
        });

        resetCreateArticle();
      } catch (error: any) {
        if ('message' in error && error.message === 'Item is not eligible for service') {
          setShowIneligibilityAlert(true);
        }

        return;
      }
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setWasPurchasedWithinNetwork(undefined);
    setBrandId(undefined);
    setOtherBrandName(undefined);
    setItemUnderWarranty(undefined);
    setAcceptedAsException(false);
    setExceptionReason(undefined);
    setShowErrors(false);
  };

  useEffect(() => {
    const brand = brands.find((brand) => brand.id === brandId);

    if (
      (wasPurchasedWithinNetwork === 'no' && !!brandId && itemUnderWarranty === 'no') ||
      (wasPurchasedWithinNetwork === 'no' && !!brandId && !brand?.contractualObligationToHandle)
    ) {
      setShowIneligibilityAlert(true);
    } else {
      setShowIneligibilityAlert(false);
    }
  }, [wasPurchasedWithinNetwork, brandId, brands, itemUnderWarranty]);

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={handleCloseModal}
      title={<Trans id="requests.new.articles.add-item-modal.title">Add an item</Trans>}
    >
      <main>
        <Stack gap="1rem">
          <Stack>
            <Label label={purchasedWithinNetworkLabel}>
              <RadioItemYesNo
                name="service-eligibility-purchased-within-network"
                ariaLabel={purchasedWithinNetworkLabel}
                value={wasPurchasedWithinNetwork}
                onChange={(value) => {
                  setWasPurchasedWithinNetwork(value);
                  setItemUnderWarranty(undefined);
                  setAcceptedAsException(false);
                  setExceptionReason(undefined);
                }}
                isInvalid={showErrors && !wasPurchasedWithinNetwork}
              />
            </Label>

            {showErrors && !wasPurchasedWithinNetwork && <PleaseSelectAnOptionError />}
          </Stack>

          {!!wasPurchasedWithinNetwork && (
            <BrandInputs
              brandOptions={brands}
              selectedBrand={brands.find((brand) => brand.id === brandId)}
              onBrandSelect={(brand) => {
                setBrandId(brand?.id);
                setOtherBrandName(undefined);
                setItemUnderWarranty(undefined);
                setAcceptedAsException(false);
                setExceptionReason(undefined);
              }}
              otherBrandName={otherBrandName}
              onOtherBrandChange={setOtherBrandName}
              showErrors={showErrors}
            />
          )}

          {wasPurchasedWithinNetwork === 'no' &&
            !!brandId &&
            brands.find((brand) => brand.id === brandId)?.contractualObligationToHandle && (
              <Stack>
                <Label label={itemUnderWarrantyLabel}>
                  <RadioItemYesNo
                    name="service-eligibility-warranty"
                    ariaLabel={itemUnderWarrantyLabel}
                    value={itemUnderWarranty}
                    onChange={(value) => {
                      setItemUnderWarranty(value);
                      setAcceptedAsException(false);
                      setExceptionReason(undefined);
                    }}
                    isInvalid={showErrors && !itemUnderWarranty}
                  />
                </Label>

                {showErrors && !itemUnderWarranty && <PleaseSelectAnOptionError />}
              </Stack>
            )}

          {showIneligibilityAlert && (
            <>
              <AlertBar
                type="error"
                title={
                  <Paragraph color="secondary">
                    <Trans id="service-eligibiliy-modal.not-eligible-error.label">
                      According to the information provided, after-sales services are not available
                      for this item.
                    </Trans>
                  </Paragraph>
                }
              />

              <Toggle
                size="small"
                alignment="left"
                ariaLabel={acceptAsExceptionLabel}
                label={<Paragraph>{acceptAsExceptionLabel}</Paragraph>}
                isSelected={acceptedAsException}
                onChange={() => {
                  setAcceptedAsException(!acceptedAsException);
                  setExceptionReason(undefined);
                }}
              />

              {acceptedAsException && (
                <Stack>
                  <TextArea
                    ariaLabel={addExceptionReasonLabel}
                    placeholder={addExceptionReasonLabel}
                    value={exceptionReason}
                    onChange={(event) => setExceptionReason(event.target.value)}
                    isInvalid={showErrors && !exceptionReason}
                  />

                  {showErrors && !exceptionReason && (
                    <Message type="error">
                      <Trans id="service-eligibiliy-modal.accept-as-exception.add-reason.error">
                        Please add an exception reason
                      </Trans>
                    </Message>
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </main>

      <Footer>
        <FooterButtons>
          <Button
            variant="primary"
            size="medium"
            onPress={handleCreateArticle}
            isLoading={isPendingCreateArticle || isSuccessCreateArticle}
            disabled={
              isPendingCreateArticle ||
              isSuccessCreateArticle ||
              (showIneligibilityAlert && !acceptedAsException)
            }
          >
            <Trans id="requests.new.articles.add-item-modal.add-button">Add an item</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
